import { STUDY_PAGINATION_SETTINGS_KEY } from './constants'

export {
  collectTemplateVisitIdsFromStudySchedule,
  getIrtControlledVisitsConfig,
  getVisitIdsForIrtMatching,
  isIrtControlledVisitRestricted,
  isIrtControlledVisitsEnabled,
  isRoleInIrtControlledList,
  shouldHideParticipantInstrumentStatus,
  isStudyVersionLive,
  isVisitInIrtControlledList,
  resolveVisitIdsForIrtMatching,
} from './irtControlledVisitsConfig'

export const CAREGIVERS_VERSION_V1 = 1
export const CAREGIVERS_VERSION_V2 = 2

export const getCaregiversVersionForStudyPromotion = config =>
  config?.study_promotion ? CAREGIVERS_VERSION_V2 : CAREGIVERS_VERSION_V1

export const syncCaregiversVersionWithStudyPromotion = config => {
  if (!config) return config
  config.caregivers = { version: getCaregiversVersionForStudyPromotion(config) }
  return config
}

export const isCaregiverV1Study = config => config?.caregivers?.version !== CAREGIVERS_VERSION_V2

export const checkIsEmailless = (currentStudy = {}) => {
  const participantDataFields = currentStudy.config?.participant_data_fields || {}
  return participantDataFields?.disabled?.some(field => field.type === 'email')
}

/**
 * Study-level multi-schedule / instrument re-use flag (GOLD-12161).
 * Presence of `config.multi_schedule` (e.g. `{}`) enables multi-schedule.
 * Absent or falsy → legacy single-schedule only.
 */
export const hasMultiScheduleFeature = (studyOrConfig = {}) => {
  const config = studyOrConfig?.config != null ? studyOrConfig.config : studyOrConfig
  return !!config?.multi_schedule
}

/**
 * Study-level gate for the GOLD-12163 scheduling-engine UI.
 * Presence of `config.scheduling_engine` (e.g. `{}`) shows the new Schedule step.
 * Absent or falsy → legacy Scheduler. Opt-in; not on by default for new studies.
 */
export const hasSchedulingEngineFeature = (studyOrConfig = {}) => {
  const config = studyOrConfig?.config != null ? studyOrConfig.config : studyOrConfig
  return !!config?.scheduling_engine
}

/** Schedule editor route segment: engine UI uses `schedule`; legacy list uses `schedules`. */
export const getInstrumentScheduleEditorSegment = studyOrConfig =>
  hasSchedulingEngineFeature(studyOrConfig) ? 'schedule' : 'schedules'

export const getInstrumentScheduleEditorPath = ({
  studyID,
  studyVersionID,
  instrumentID,
  studyOrConfig,
  search = '',
}) => {
  const segment = getInstrumentScheduleEditorSegment(studyOrConfig)
  const base = `/studies/${studyID}/${studyVersionID}/instruments/${instrumentID}/${segment}`
  return search ? `${base}${search.startsWith('?') ? search : `?${search}`}` : base
}

/** True when schedule uses the multi-schedule array shape (legacy single schedule is a plain object). */
export const isMultiSchedulePayload = schedule => Array.isArray(schedule) && schedule.length > 0

/** Uploaded instrument metadata indicates multi-schedule (2+ schedules or instrument_reuse). */
export const isMultiScheduleInstrumentUpload = (metadata = {}) => {
  const schedule = metadata?.schedule
  const hasMultipleSchedules = Array.isArray(schedule) && schedule.length > 1
  return !!metadata.instrument_reuse || hasMultipleSchedules
}

/**
 * When multi-schedule is disabled, collapse schedule to a single object for API payloads.
 * Leaves arrays of length 0/1 as object (first element or empty object).
 */
export const coerceScheduleForStudyFlag = (schedule, hasMultiSchedule) => {
  if (hasMultiSchedule) return schedule
  if (Array.isArray(schedule)) {
    return schedule[0] || {}
  }
  return schedule
}

export const persistStudyPaginationSettings = (userId, settings) => {
  if (!userId) {
    return
  }
  localStorage.setItem(`${STUDY_PAGINATION_SETTINGS_KEY}_${userId}`, JSON.stringify(settings))
}

export const retrieveStudyPaginationSettings = userId => {
  if (!userId) {
    return
  }
  return JSON.parse(localStorage.getItem(`${STUDY_PAGINATION_SETTINGS_KEY}_${userId}`) || null)
}

export const removeStudyPaginationSettings = key => {
  if (!key) {
    return
  }
  localStorage.removeItem(key)
}

export const clearStudyPaginationSettings = () => {
  const allItems = { ...localStorage }
  const studyPaginationKeys = Object.keys(allItems).filter(key => key.includes(STUDY_PAGINATION_SETTINGS_KEY))
  studyPaginationKeys.forEach(key => {
    removeStudyPaginationSettings(key)
  })
}

export const getStudyID = location => {
  return location?.pathname?.split('/')[2]
}

/**
 * Study id for in-study navigation and permissions while `currentStudy` may still
 * reflect a previously opened study (e.g. deep link from a study card icon).
 */
export const resolveRouteStudyId = ({ location, paramsStudyID, currentStudyId } = {}) => {
  const routeStudyId = getStudyID(location) ?? paramsStudyID
  if (routeStudyId != null && routeStudyId !== '') {
    const numericId = Number(routeStudyId)
    return Number.isNaN(numericId) ? routeStudyId : numericId
  }
  return currentStudyId
}

export const getStudyVersionID = location => {
  if (!location?.pathname) return null
  return location.pathname.split('/')[3]
}

export const getVersionedQuery = studyVersionID =>
  studyVersionID !== 'live' ? `?study_version_id=${studyVersionID}` : ''

export const isVersionUatOrLive = (studyVersionList, studyVersionID) => {
  if (!studyVersionList || studyVersionList.length == 0) return false
  const currentVersion = studyVersionList?.find(version => version?.version_id === studyVersionID)
  const status = currentVersion?.version_status?.toLowerCase()
  return status === 'uat' || status === 'live'
}

export const getStudyVersionStatus = (studyVersionList, studyVersionID) => {
  if (!studyVersionList || studyVersionList.length == 0) return 'dev'
  return studyVersionList?.find(version => version?.version_id === studyVersionID)?.version_status?.toLowerCase()
}

export const getClinroUrl = () => {
  const adminUrl = window.location.origin
  return adminUrl.includes('admin') ? adminUrl.replace('admin', 'clinro') : 'https://clinro.develop.d-3.io'
}
